import { Link } from "@tanstack/react-router";

import { BadgeIcon } from "@/components/Icon/BadgeIcon";
import { HStack } from "@/components/Layout";
import { Text } from "@/components/Typography/Text";

type Step = {
  label: string;
  hash: string;
};

type Props = {
  baseLink: string;
  currentHash: string;
  isError?: boolean;
  steps: Step[];
  step: Step;
  stepIndex: number;
};

const AddStepperNavigationStepItem = ({
  baseLink,
  currentHash,
  isError,
  steps,
  step,
  stepIndex,
}: Props) => {
  const status = (() => {
    const stepOrder = steps.findIndex((step) => step.hash === currentHash);
    if (stepIndex === stepOrder) return "active";
    if (stepIndex < stepOrder) return "completed";
    return "pending";
  })();

  const isActive = status === "active";
  const isPending = status === "pending";
  const isCompleted = status === "completed";

  const styles = (() => {
    if (isActive && isError) {
      return {
        container:
          "p-3 text-error-on-muted-fill rounded-md bg-error-muted-fill font-semibold",
        badgeType: "error" as const,
        textColor: "text-error-on-muted-fill",
      };
    }
    if (isActive) {
      return {
        container:
          "p-3 rounded-md bg-primary-muted-fill font-semibold text-primary",
        badgeType: "primary" as const,
        textColor: "text-primary-on-neutral-fill",
      };
    }
    if (isCompleted) {
      return {
        container:
          "p-3 text-muted-foreground hover:bg-primary-muted-fill hover:text-primary hover:shadow-xs",
        badgeType: "check" as const,
        textColor: "text-neutral-muted-on-fill",
      };
    }
    return {
      container:
        "p-3 text-muted-foreground hover:bg-primary-muted-fill hover:text-primary hover:shadow-xs",
      badgeType: "neutral" as const,
      textColor: "text-neutral-muted-on-fill",
    };
  })();

  return (
    <Link
      to={baseLink}
      hash={step.hash}
      disabled={isPending}
      className={styles.container}
    >
      <HStack className="items-center gap-1.5">
        <BadgeIcon count={stepIndex + 1} type={styles.badgeType} />
        <Text variant="label" className={`text-sm ${styles.textColor}`}>
          {step.label}
        </Text>
      </HStack>
    </Link>
  );
};

export default AddStepperNavigationStepItem;
